import React, { useState, useEffect, useMemo, Dispatch, SetStateAction } from "react";
import { useListsByAgent } from "../api";
import { Typography, Stack, LinearProgress } from "@mui/material";
import styled from "@emotion/styled";
import AgentChip from "./AgentChip";
import { monitorForElements } from "@atlaskit/pragmatic-drag-and-drop/element/adapter";
import ListItem from "./ListItem";
import { ErrorHandler } from "../../list-manager/error-handling";
import { AxiosError } from "axios";

type DragListData = List;

type AgentListPriorityProps = {
  selectedAgent: Agent | null | undefined;
  newListsOrder: List[];
  setNewListsOrder: Dispatch<SetStateAction<List[]>>;
  oldListsOrder: List[];
  setOldListsOrder: Dispatch<SetStateAction<List[]>>;
  isPending: boolean;
  isSuccess: boolean;
  saveError?: AxiosError;
};

export default function AgentListPriority({
  selectedAgent,
  newListsOrder,
  setNewListsOrder,
  oldListsOrder,
  setOldListsOrder,
  isPending,
  isSuccess,
  saveError,
}: AgentListPriorityProps) {
  const { data, isLoading, isFetching, error: listsError } = useListsByAgent({
    agent_id: selectedAgent?.id,
  });

  useEffect(() => {
    if (data) {
      setNewListsOrder(data?.map((rep, i) => ({ ...rep, rank: i + 1 })) || []);
      setOldListsOrder(data?.map((rep, i) => ({ ...rep, rank: i + 1 })) || []);
    }
  }, [data]);

  useEffect(() => {
    return monitorForElements({
      onDrop: ({ location: { initial, current } }) => {
        const draggedList = initial.dropTargets[0]?.data as
          | DragListData
          | undefined;
        const droppedOntoList = current.dropTargets[0]?.data as
          | DragListData
          | undefined;

        if (
          !draggedList ||
          !droppedOntoList ||
          draggedList.id === droppedOntoList.id
        )
          return;

        setNewListsOrder((prevOrder) => {
          const filtered = prevOrder.filter(
            (list) => list.id !== draggedList.id,
          );
          const dropIndex = filtered.findIndex(
            (list) => list.id === droppedOntoList.id,
          );
          filtered.splice(dropIndex, 0, draggedList);
          return filtered.map((list, i) => ({ ...list, rank: i + 1 }));
        });
      },
    });
  }, []);

  const hasChanges = React.useMemo(() => {
    if (newListsOrder.length !== oldListsOrder.length) return true;
    return newListsOrder.some((list, idx) => list.id !== oldListsOrder[idx].id);
  }, [newListsOrder, oldListsOrder]);

  if (!selectedAgent)
    return (
      <Wrapper style={{ paddingTop: "10%", alignItems: "center" }}>
        <Typography style={{ color: "gray", fontSize: 14 }}>
          Select an Agent to view assigned lists
        </Typography>
      </Wrapper>
    );

  if (isLoading || isFetching)
    return (
      <Wrapper style={{ paddingTop: "10%", alignItems: "center" }}>
        <Typography style={{ color: "gray", fontSize: 14 }}>
          Loading...
        </Typography>
        <LinearProgress style={{width: "50%"}} />
      </Wrapper>
    );
  if (isPending)
    return (
      <Wrapper style={{ paddingTop: "10%", alignItems: "center" }}>
        <Typography style={{ color: "gray", fontSize: 14 }}>
          Submitting...
        </Typography>
        <LinearProgress style={{width: "50%"}} />
      </Wrapper>
    );
  if (!data || data.length === 0)
    return (
      <Wrapper style={{ paddingTop: "10%", alignItems: "center" }}>
        <Typography style={{ color: "gray", fontSize: 14 }}>
          No Lists Found for {selectedAgent?.email}
        </Typography>
      </Wrapper>
    );

  return (
    <Wrapper>
      <Stack height={50}>
        <AgentChip agent={selectedAgent} />
      </Stack>
      {isSuccess && (
        <Typography
          style={{
            fontSize: 12,
            paddingLeft: 5,
            color: "green",
            fontWeight: "bold",
          }}
        >
          New Priority Saved!
        </Typography>
      )}
      <ErrorHandler
        error={(saveError as AxiosError) || (listsError as AxiosError)}
      />
      <Typography
        style={{
          padding: 5,
          fontSize: 12,
          color: "gray",
          fontWeight: "bold",
        }}
      >
        Drag and drop a list to change the order
      </Typography>
      <Stack paddingBottom={3}>
        {newListsOrder.map((l: List, index) => (
          <ListItem
            key={l.id}
            list={l}
            index={index}
            isFirst={index === 0}
            isLast={index === newListsOrder.length - 1}
            onMoveUp={() => {
              if (index === 0) return;
              setNewListsOrder((prev) => {
                const copy = [...prev];
                const temp = copy[index - 1];
                copy[index - 1] = copy[index];
                copy[index] = temp;
                return copy.map((list, i) => ({
                  ...list,
                  rank: i + 1,
                }));
              });
            }}
            onMoveDown={() => {
              if (index === newListsOrder.length - 1) return;
              setNewListsOrder((prev) => {
                const copy = [...prev];
                const temp = copy[index + 1];
                copy[index + 1] = copy[index];
                copy[index] = temp;
                return copy.map((list, i) => ({
                  ...list,
                  rank: i + 1,
                }));
              });
            }}
          />
        ))}
      </Stack>
    </Wrapper>
  );
}

const Wrapper = styled.div`
  width: 30vw;
  display: flex;
  gap: 5px;
  flex-direction: column;
  height: 100%;
  padding: 20px;
  padding-top: 15px;
`;
